import React from 'react';
import Icon from '../components/Icon';
import Seo from '../components/Seo';
import FAQ from '../components/FAQ';

const SITE_URL = 'https://bocadelraypath.com';
const INDEED_URL = 'https://www.indeed.com/cmp/Boca-Delray-Pathology-Laboratory';
const STATE_PROGRAM_LIST = 'https://floridasclinicallabs.gov/forms/clp-training-program-list.pdf';

const skills = [
  {
    title: 'Accessioning & Tracking',
    icon: 'fileCheck',
    text: 'Receiving specimens, matching them to requisitions, and keeping every block and slide traceable to the right patient.'
  },
  {
    title: 'Fixation & Processing',
    icon: 'droplet',
    text: 'How formalin fixation and tissue processing preserve tissue, and what goes wrong when either is rushed.'
  },
  {
    title: 'Embedding',
    icon: 'layers',
    text: 'Orienting tissue in paraffin so the pathologist sees the right plane, a skill small GI biopsies demand every day.'
  },
  {
    title: 'Microtomy',
    icon: 'slide',
    text: 'Cutting thin, even sections on the microtome and mounting them cleanly on slides.'
  },
  {
    title: 'Staining',
    icon: 'microscope',
    text: 'Routine H&E staining, plus how special stains and immunohistochemistry support a diagnosis.'
  },
  {
    title: 'Quality & Safety',
    icon: 'shield',
    text: 'Quality control, laboratory safety, and the HIV/AIDS and medical error prevention instruction Florida requires.'
  }
];

const trainingFaqs = [
  {
    question: 'Is the histology training program state-approved?',
    answer: 'Yes. Boca-Delray Pathology Laboratories has held a Clinical Laboratory Training Program license in Histology since 2007 from the Florida Department of Health, Division of Medical Quality Assurance. Training programs are approved by the Florida Board of Clinical Laboratory Personnel under Chapter 483, Part III, Florida Statutes, and Rule 64B3-3, Florida Administrative Code.'
  },
  {
    question: 'Are there other histology training programs in South Florida?',
    answer: 'Yes, at colleges. According to Florida Department of Health license records (October 2026), the other state-licensed histology training programs in Palm Beach, Broward, and Miami-Dade counties are run by colleges. Boca-Delray Pathology Laboratories is the only clinical laboratory in South Florida with one.'
  },
  {
    question: 'Where does the training take place?',
    answer: 'In our working anatomic pathology laboratory at 5150 Linton Blvd, Suite 250, Delray Beach, Florida, alongside the histotechnicians and pathologists who handle our daily caseload.'
  },
  {
    question: 'How does the program relate to a Florida histology license?',
    answer: 'Florida licenses histology technicians who hold national HT(ASCP) certification. Our program provides supervised, hands-on histology training in a clinical laboratory; ask us how it fits your own path to certification and licensure.'
  },
  {
    question: 'How do I apply?',
    answer: 'Openings become available once current trainees complete the one-year program. We post them on Indeed when they open. For questions about the program, call the laboratory at (561) 638-7577.'
  }
];

const programSchema = {
  '@context': 'https://schema.org',
  '@type': 'EducationalOccupationalProgram',
  '@id': `${SITE_URL}/histology-training#program`,
  name: 'Histology Training Program',
  description: 'State-licensed clinical laboratory training program in histology (licensed by the Florida Department of Health since 2007), taught in a working anatomic pathology laboratory in Delray Beach, Florida; the only clinical laboratory in South Florida with a state-licensed histology training program.',
  url: `${SITE_URL}/histology-training`,
  provider: { '@id': `${SITE_URL}/#organization` },
  educationalProgramMode: 'in-person',
  occupationalCategory: '29-2012.01'
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: trainingFaqs.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer }
  }))
};

function HistologyTraining() {
  return (
    <div className="training-page">
      <Seo
        title="Histology Training Program in South Florida"
        path="/histology-training"
        description="Histology training program licensed by the Florida Department of Health: hands-on training from processing to staining in our Delray Beach pathology lab."
        jsonLd={[programSchema, faqSchema]}
      />
      <section className="page-banner">
        <p className="section-label">Careers &amp; Training</p>
        <h1>Histology Training Program</h1>
        <p>We train histotechnicians inside a working anatomic pathology laboratory. Trainees learn to turn biopsies and surgical specimens into the slides our pathologists diagnose from, under a program licensed by the State of Florida since 2007.</p>
      </section>

      <section>
        <p className="section-label">Only One of Its Kind</p>
        <h2>The Only Clinical Lab in South Florida With a Histology Training Program</h2>
        <p>Among the state-licensed histology training programs in South Florida (Palm Beach, Broward, and Miami-Dade counties), ours is the only one run by a clinical laboratory rather than a college, so trainees learn on real specimens, in a real laboratory, from the people doing the work every day.</p>
        <p className="source-note">Source: Florida Department of Health license records for Clinical Laboratory Training Programs, as of October 2026.</p>
      </section>

      <section className="quality-assurance">
        <p className="section-label">State Licensed</p>
        <h2>Approved by the Florida Board of Clinical Laboratory Personnel</h2>
        <div className="quality-grid">
          <div className="quality-item">
            <Icon name="shield" className="card-icon" />
            <h3>State-Licensed Program</h3>
            <p>Clinical Laboratory Training Program license, qualification: Histology. First licensed in 2007.</p>
          </div>
          <div className="quality-item">
            <Icon name="flag" className="card-icon" />
            <h3>Florida Department of Health</h3>
            <p>Issued by the Division of Medical Quality Assurance. Programs are approved by the Board of Clinical Laboratory Personnel.</p>
          </div>
          <div className="quality-item">
            <Icon name="fileCheck" className="card-icon" />
            <h3>Chapter 483, F.S.</h3>
            <p>Approved under Chapter 483, Part III, Florida Statutes, and Rule 64B3-3, Florida Administrative Code.</p>
          </div>
          <div className="quality-item">
            <Icon name="microscope" className="card-icon" />
            <h3>Clinical Practicum</h3>
            <p>State rules require approved programs to include a practicum in a clinical laboratory. Ours happens in our own lab.</p>
          </div>
        </div>
        <p className="section-more"><a href={STATE_PROGRAM_LIST} className="learn-more" target="_blank" rel="noopener noreferrer">See the state’s list of approved training programs</a></p>
      </section>

      <section>
        <p className="section-label">The Work</p>
        <h2>What Histotechnicians Do</h2>
        <p>Every tissue diagnosis starts with a slide. Histotechnicians take a biopsy or surgical specimen from the moment it arrives in formalin to a thin, stained section on glass, ready for the pathologist’s microscope. The quality of that slide directly affects what the pathologist can see, which makes histology one of the most important, and least visible, jobs in patient care.</p>
      </section>

      <section className="collaboration">
        <p className="section-label">Curriculum</p>
        <h2>What Trainees Learn</h2>
        <div className="feature-grid">
          {skills.map((skill) => (
            <div key={skill.title} className="feature-item">
              <Icon name={skill.icon} className="card-icon" />
              <h3>{skill.title}</h3>
              <p>{skill.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="section-label">Who It’s For</p>
        <h2>Starting a Career in Histology</h2>
        <div className="service-grid">
          <div className="service-item">
            <h3>New to the Lab</h3>
            <p>People with a science background who want a hands-on laboratory career in patient care.</p>
          </div>
          <div className="service-item">
            <h3>Already in Healthcare</h3>
            <p>Lab assistants, aides, and other healthcare workers ready to move into histology.</p>
          </div>
          <div className="service-item">
            <h3>Career Changers</h3>
            <p>Detail-oriented people who want meaningful, behind-the-scenes work in medicine.</p>
          </div>
        </div>
      </section>

      <FAQ items={trainingFaqs} title="Training Program Questions" />

      <section className="cta-section">
        <p className="section-label">Future Openings</p>
        <h2>Interested in Training With Us?</h2>
        <p>Openings become available once current trainees complete their one-year program. Check for future openings on Indeed, where we post them as soon as they open.</p>
        <div className="cta-buttons">
          <a href={INDEED_URL} target="_blank" rel="noopener noreferrer" className="cta-button">View Openings on Indeed</a>
          <a href="tel:+15616387577" className="cta-button secondary">Call (561) 638-7577</a>
        </div>
      </section>
    </div>
  );
}

export default HistologyTraining;
